import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { api } from '../client';
import { ME_KEY, isMeKey } from '../auth/use-me';
import { useToast } from '../shell/toast';
import { navigate } from '../shell/use-route';
import { forgetInvite, pendingInvite } from './workspace-form';
import type { WorkspaceForm } from './workspace-form';

/**
 * Drops every cached screen — none of it belongs to the workspace being
 * entered — except who is signed in, which is fetched again before returning.
 * Removing `me` too would leave the app showing the old answer: a removed
 * query is not refetched by invalidating it.
 */
async function enter(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  queryClient.removeQueries({ predicate: (query) => !isMeKey(query.queryKey) });
  await queryClient.refetchQueries({ queryKey: ME_KEY });
  navigate({ page: 'overview' });
}

export function useSelectWorkspace(): UseMutationResult<void, Error, { id: string; name: string }> {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async ({ id }) => {
      const result = await api.workspaces.select({ params: { workspaceId: id }, body: {} });
      if (result.status !== 200) throw new Error('You are no longer in that workspace.');
    },
    onSuccess: async (_, { name }) => {
      await enter(queryClient);
      toast(`Switched to ${name}`);
    },
    onError: (error) => toast(error.message),
  });
}

/**
 * Once signed in, an invitation the tab arrived with is accepted, once. The
 * API only lets the address it was sent to use it, so a forwarded link says so.
 */
export function useAcceptPendingInvite(signedIn: boolean): void {
  const queryClient = useQueryClient();
  const toast = useToast();
  const tried = useRef(false);

  useEffect(() => {
    const token = pendingInvite();
    if (!signedIn || token === undefined || tried.current) return;
    tried.current = true;
    void (async () => {
      const result = await api.workspaces.accept({ body: { token } }).catch(() => undefined);
      forgetInvite();
      if (result?.status === 200) {
        await enter(queryClient);
        toast(`You joined ${result.body.workspace.name}`);
      } else {
        toast('That invitation is used, expired, or for another email address.');
      }
    })();
  }, [signedIn, queryClient, toast]);
}

type Created = Awaited<ReturnType<typeof api.workspaces.create>> & { status: 201 };

/** Creates a workspace and enters it. `first`: the signup step, which also names the person. */
export function useCreateWorkspace(
  first: boolean,
  onDone?: () => void,
): UseMutationResult<Created['body']['workspace'], Error, WorkspaceForm> {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (values: WorkspaceForm) => {
      const personName = values.personName.trim();
      const result = await api.workspaces.create({
        body: { name: values.name, ...(first && personName !== '' ? { personName } : {}) },
      });
      if (result.status !== 201) throw new Error('That name cannot be used. Try another.');
      return result.body.workspace;
    },
    onSuccess: async (workspace) => {
      await enter(queryClient);
      toast(`${workspace.name} is ready`);
      onDone?.();
    },
  });
}
